import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  Button,
  Badge,
} from "@asm/ui";
import {
  Grid3X3,
  RefreshCw,
  AlertTriangle,
  Lock,
  LockOpen,
  Server,
  Globe,
  Shield,
  Info,
  ExternalLink,
  X,
  Cloud,
  Building2,
} from "lucide-react";
import { riskApi, type ServiceMatrix as ServiceMatrixType, type PortCell, type IpRow } from "@/lib/api";
import { cn } from "@asm/ui";

// Cloud provider icons (SVG as data URIs for common providers)
const cloudProviderIcons: Record<string, { icon: string; color: string; name: string }> = {
  // AWS variations
  "amazon": { icon: "/icons/aws.svg", color: "#FF9900", name: "AWS" },
  "aws": { icon: "/icons/aws.svg", color: "#FF9900", name: "AWS" },
  "amazon.com": { icon: "/icons/aws.svg", color: "#FF9900", name: "AWS" },
  "amazon web services": { icon: "/icons/aws.svg", color: "#FF9900", name: "AWS" },
  // Azure variations
  "microsoft": { icon: "/icons/azure.svg", color: "#0078D4", name: "Azure" },
  "azure": { icon: "/icons/azure.svg", color: "#0078D4", name: "Azure" },
  "microsoft azure": { icon: "/icons/azure.svg", color: "#0078D4", name: "Azure" },
  "microsoft corporation": { icon: "/icons/azure.svg", color: "#0078D4", name: "Azure" },
  // GCP variations
  "google": { icon: "/icons/gcp.svg", color: "#4285F4", name: "GCP" },
  "gcp": { icon: "/icons/gcp.svg", color: "#4285F4", name: "GCP" },
  "google cloud": { icon: "/icons/gcp.svg", color: "#4285F4", name: "GCP" },
  "google llc": { icon: "/icons/gcp.svg", color: "#4285F4", name: "GCP" },
  // DigitalOcean
  "digitalocean": { icon: "/icons/digitalocean.svg", color: "#0080FF", name: "DigitalOcean" },
  // Cloudflare
  "cloudflare": { icon: "/icons/cloudflare.svg", color: "#F38020", name: "Cloudflare" },
  // Linode/Akamai
  "linode": { icon: "/icons/linode.svg", color: "#00A95C", name: "Linode" },
  "akamai": { icon: "/icons/akamai.svg", color: "#0096D6", name: "Akamai" },
  // OVH
  "ovh": { icon: "/icons/ovh.svg", color: "#123F6D", name: "OVH" },
  // Hetzner
  "hetzner": { icon: "/icons/hetzner.svg", color: "#D50C2D", name: "Hetzner" },
  // Vultr
  "vultr": { icon: "/icons/vultr.svg", color: "#007BFC", name: "Vultr" },
  // Oracle
  "oracle": { icon: "/icons/oracle.svg", color: "#F80000", name: "Oracle Cloud" },
  // IBM
  "ibm": { icon: "/icons/ibm.svg", color: "#1F70C1", name: "IBM Cloud" },
  // Alibaba
  "alibaba": { icon: "/icons/alibaba.svg", color: "#FF6A00", name: "Alibaba Cloud" },
};

// Find cloud provider info from provider string or ASN name
function getCloudProviderInfo(cloudProvider?: string, asnName?: string): { icon: string; color: string; name: string } | null {
  const searchTerms = [
    cloudProvider?.toLowerCase(),
    asnName?.toLowerCase(),
  ].filter(Boolean) as string[];

  for (const term of searchTerms) {
    // Direct match
    if (cloudProviderIcons[term]) {
      return cloudProviderIcons[term];
    }
    // Partial match
    for (const [key, value] of Object.entries(cloudProviderIcons)) {
      if (term.includes(key) || key.includes(term)) {
        return value;
      }
    }
  }
  return null;
}

// Common port descriptions
const portDescriptions: Record<number, string> = {
  21: "FTP",
  22: "SSH",
  23: "Telnet",
  25: "SMTP",
  53: "DNS",
  80: "HTTP",
  110: "POP3",
  143: "IMAP",
  443: "HTTPS",
  445: "SMB",
  993: "IMAPS",
  995: "POP3S",
  1433: "MSSQL",
  1521: "Oracle",
  3306: "MySQL",
  3389: "RDP",
  5432: "PostgreSQL",
  5900: "VNC",
  6379: "Redis",
  8080: "HTTP Alt",
  8443: "HTTPS Alt",
  27017: "MongoDB",
};

// Sensitive ports that should be highlighted
const sensitivePorts = new Set([22, 23, 3306, 5432, 6379, 27017, 3389, 5900, 1433, 1521]);

// Security recommendations by port
const securityRecommendations: Record<number, string> = {
  21: "FTP transmits data in cleartext. Consider using SFTP (port 22) or FTPS instead.",
  22: "SSH is exposed to the internet. Restrict access via firewall, use key-based auth, and consider fail2ban.",
  23: "Telnet is unencrypted and insecure. Replace with SSH immediately.",
  25: "SMTP should be authenticated and use TLS. Consider using port 587 for submission.",
  80: "HTTP is unencrypted. Redirect to HTTPS and implement HSTS.",
  445: "SMB/CIFS exposed to internet is high risk. Block at firewall unless required.",
  1433: "MSSQL exposed to internet. Move behind VPN or firewall.",
  1521: "Oracle DB exposed. Restrict to private network only.",
  3306: "MySQL exposed to internet. Use SSL/TLS, restrict to private network.",
  3389: "RDP exposed is a major attack vector. Use VPN, Network Level Auth, or RD Gateway.",
  5432: "PostgreSQL exposed. Configure pg_hba.conf properly, enable SSL.",
  5900: "VNC is often unencrypted. Use SSH tunneling or replace with more secure solution.",
  6379: "Redis without auth is critical risk. Enable AUTH, bind to localhost or private network.",
  8080: "Alternative HTTP port often lacks security headers. Ensure same security as port 80.",
  27017: "MongoDB exposed. Enable authentication, bind to private network.",
};

// Port severity levels for color coding
function getPortSeverity(port: number, encrypted: boolean): "critical" | "high" | "medium" | "low" | "safe" {
  if (sensitivePorts.has(port) && !encrypted) {
    if ([6379, 27017, 3389, 5900, 23].includes(port)) return "critical";
    if ([22, 3306, 5432, 1433, 1521, 445].includes(port)) return "high";
    return "medium";
  }
  if (!encrypted && [80, 8080].includes(port)) return "low";
  return "safe";
}

interface SelectedPort {
  cell: PortCell;
  ipRow: IpRow;
}

export default function ServiceMatrix() {
  const [selectedPort, setSelectedPort] = useState<SelectedPort | null>(null);

  const {
    data: matrix,
    isLoading,
    refetch,
    error,
  } = useQuery({
    queryKey: ["service-matrix"],
    queryFn: () => riskApi.getServiceMatrix(),
    staleTime: 30_000,
  });

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Grid3X3 className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold">Unable to load matrix</h3>
        <p className="text-muted-foreground mt-1">
          {error instanceof Error ? error.message : "An error occurred"}
        </p>
        <Button onClick={() => refetch()} variant="outline" className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-4">
      {/* Main Content */}
      <div className={cn(
        "flex-1 space-y-6 transition-all duration-300",
        selectedPort ? "mr-[360px]" : ""
      )}>
        {/* Legend */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded bg-green-500/20 border border-green-500" />
                <span className="text-sm">Open (Encrypted)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded bg-yellow-500/20 border border-yellow-500" />
                <span className="text-sm">Open (Unencrypted)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded bg-red-500/20 border border-red-500" />
                <span className="text-sm">Sensitive Service</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded bg-muted border" />
                <span className="text-sm">Closed/Filtered</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => refetch()} className="ml-auto">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Matrix */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Grid3X3 className="h-5 w-5" />
              Service Exposure Matrix
            </CardTitle>
            <CardDescription>
              Port status across all discovered IP addresses. Click any open port to see details.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : !matrix || matrix.ips.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                <Server className="h-12 w-12 opacity-50 mb-4" />
                <p>No IP addresses with port data</p>
                <p className="text-sm">Run port scans to populate this matrix</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="sticky left-0 bg-background z-10 p-2 text-left font-medium text-sm border-b min-w-[220px]">
                        IP / Subdomains
                      </th>
                      {matrix.ports.map((port) => (
                        <th
                          key={port}
                          className={cn(
                            "p-2 text-center font-medium text-xs border-b min-w-[60px]",
                            sensitivePorts.has(port) && "bg-red-500/5"
                          )}
                        >
                          <div>{port}</div>
                          <div className="text-[10px] text-muted-foreground font-normal">
                            {portDescriptions[port] || ""}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.ips.map((row) => (
                      <tr key={row.asset_id} className="hover:bg-accent/30">
                        <td className="sticky left-0 bg-background z-10 p-2 border-b">
                          <Link
                            to={`/assets/${row.asset_id}`}
                            className="hover:underline"
                          >
                            <div className="flex items-center gap-2">
                              <CloudProviderIcon
                                isCloud={row.is_cloud}
                                cloudProvider={row.cloud_provider}
                                asnName={row.asn_name}
                              />
                              <span className="font-mono text-sm">{row.ip}</span>
                              {row.is_cloud && row.cloud_provider && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                                  {getCloudProviderInfo(row.cloud_provider, row.asn_name)?.name || row.cloud_provider}
                                </Badge>
                              )}
                            </div>
                          </Link>
                          {row.hostname && (
                            <div className="text-xs text-muted-foreground truncate max-w-[200px] ml-6">
                              {row.hostname}
                            </div>
                          )}
                          {row.asn_name && !row.is_cloud && (
                            <div className="text-xs text-muted-foreground truncate max-w-[200px] ml-6 flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {row.asn_name}
                            </div>
                          )}
                          {row.subdomains && row.subdomains.length > 0 && (
                            <div className="mt-1 ml-6 space-y-0.5">
                              {row.subdomains.slice(0, 3).map((sub) => (
                                <div key={sub} className="flex items-center gap-1 text-xs text-blue-500">
                                  <Globe className="h-3 w-3" />
                                  <span className="truncate max-w-[180px]">{sub}</span>
                                </div>
                              ))}
                              {row.subdomains.length > 3 && (
                                <div className="text-xs text-muted-foreground">
                                  +{row.subdomains.length - 3} more
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        {row.cells.map((cell, idx) => (
                          <td
                            key={`${row.asset_id}-${matrix.ports[idx]}`}
                            className="p-1 border-b text-center"
                          >
                            <PortCellDisplay
                              cell={cell}
                              isSensitive={sensitivePorts.has(cell.port)}
                              isSelected={
                                selectedPort?.cell.port === cell.port &&
                                selectedPort?.ipRow.asset_id === row.asset_id
                              }
                              onClick={() => {
                                if (cell.state === "open") {
                                  // Toggle selection if clicking same port
                                  if (
                                    selectedPort?.cell.port === cell.port &&
                                    selectedPort?.ipRow.asset_id === row.asset_id
                                  ) {
                                    setSelectedPort(null);
                                  } else {
                                    setSelectedPort({ cell, ipRow: row });
                                  }
                                }
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        {matrix && matrix.ips.length > 0 && (
          <div className="grid gap-4 md:grid-cols-4">
            <StatCard
              title="Total IPs"
              value={matrix.ips.length}
              description="With port data"
            />
            <StatCard
              title="Open Ports"
              value={countOpenPorts(matrix)}
              description="Across all IPs"
            />
            <StatCard
              title="Sensitive Exposed"
              value={countSensitivePorts(matrix)}
              description="Critical services"
              highlight
            />
            <StatCard
              title="Unencrypted"
              value={countUnencryptedPorts(matrix)}
              description="Without TLS"
            />
          </div>
        )}
      </div>

      {/* Side Panel - No overlay, just slides in */}
      <div
        className={cn(
          "fixed top-0 right-0 h-full w-[360px] bg-background border-l shadow-lg z-40",
          "transform transition-transform duration-300 ease-in-out",
          "overflow-y-auto",
          selectedPort ? "translate-x-0" : "translate-x-full"
        )}
      >
        {selectedPort && (
          <PortDetailsPanel
            cell={selectedPort.cell}
            ipRow={selectedPort.ipRow}
            onClose={() => setSelectedPort(null)}
          />
        )}
      </div>
    </div>
  );
}

// Cloud provider icon component with inline SVGs for major providers
function CloudProviderIcon({
  isCloud,
  cloudProvider,
  asnName,
  className = "h-4 w-4",
}: {
  isCloud: boolean;
  cloudProvider?: string;
  asnName?: string;
  className?: string;
}) {
  const providerInfo = getCloudProviderInfo(cloudProvider, asnName);

  // If it's a cloud provider, show the appropriate icon
  if (isCloud && providerInfo) {
    const providerKey = providerInfo.name.toLowerCase();

    // AWS
    if (providerKey === "aws") {
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M6.763 10.036c0 .296.032.535.088.71.064.176.144.368.256.576.04.063.056.127.056.183 0 .08-.048.16-.152.24l-.503.335a.383.383 0 01-.208.072c-.08 0-.16-.04-.239-.112a2.47 2.47 0 01-.287-.375 6.18 6.18 0 01-.248-.471c-.622.734-1.405 1.101-2.347 1.101-.67 0-1.205-.191-1.596-.574-.391-.384-.59-.896-.59-1.533 0-.678.239-1.23.726-1.644.487-.415 1.133-.623 1.955-.623.272 0 .551.024.846.064.296.04.6.104.918.176v-.583c0-.607-.127-1.03-.375-1.277-.255-.248-.686-.367-1.3-.367-.28 0-.568.031-.863.103-.296.072-.583.16-.863.279a2.07 2.07 0 01-.255.104c-.048.016-.08.024-.104.024-.096 0-.144-.072-.144-.224v-.392c0-.112.016-.2.056-.256.04-.064.112-.128.216-.192a4.61 4.61 0 01.999-.36A4.906 4.906 0 016.02 5.32c.967 0 1.676.22 2.123.662.455.44.679 1.109.679 2.003v2.052h-.06zm-3.24 1.205c.263 0 .534-.048.822-.144.287-.096.543-.271.758-.51.128-.152.224-.32.272-.512.047-.191.08-.423.08-.694v-.335a6.66 6.66 0 00-.735-.136 6.02 6.02 0 00-.75-.048c-.535 0-.926.104-1.19.32-.263.216-.39.518-.39.918 0 .375.095.655.295.846.191.2.47.295.838.295zm6.41.862c-.12 0-.2-.016-.255-.056-.056-.032-.104-.104-.144-.208l-1.614-5.31a1.07 1.07 0 01-.056-.28c0-.111.056-.175.167-.175h.79c.128 0 .216.016.263.056.056.032.096.104.136.208l1.157 4.56 1.077-4.56c.032-.112.072-.176.128-.208.056-.04.151-.056.27-.056h.647c.128 0 .216.016.271.056.056.032.104.104.128.208l1.093 4.615 1.19-4.615c.04-.112.088-.176.136-.208a.481.481 0 01.263-.056h.751c.112 0 .176.056.176.175 0 .032-.008.064-.016.104a1.39 1.39 0 01-.04.183l-1.654 5.31c-.04.112-.088.176-.143.208a.526.526 0 01-.256.056h-.695c-.128 0-.216-.016-.27-.056-.056-.04-.104-.104-.128-.216l-1.069-4.439-1.061 4.43c-.032.112-.072.176-.128.216-.056.04-.15.056-.27.056h-.694z"
            fill="#FF9900"
          />
          <path
            d="M21.726 16.212c-2.728 2.015-6.686 3.088-10.095 3.088-4.775 0-9.078-1.765-12.33-4.703-.256-.231-.024-.55.28-.367 3.514 2.044 7.858 3.272 12.346 3.272 3.027 0 6.357-.63 9.422-1.931.463-.2.853.303.377.641z"
            fill="#FF9900"
          />
          <path
            d="M22.805 14.97c-.35-.447-2.303-.216-3.187-.112-.263.032-.303-.2-.063-.375 1.558-1.093 4.118-.774 4.415-.408.296.375-.08 2.96-1.542 4.198-.224.184-.439.088-.343-.16.335-.83 1.069-2.696.72-3.143z"
            fill="#FF9900"
          />
        </svg>
      );
    }

    // Azure
    if (providerKey === "azure") {
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M13.05 4.24L6.56 18.05a.5.5 0 00.46.7h8.1a.5.5 0 00.44-.26l6.49-13.81a.5.5 0 00-.45-.74H13.5a.5.5 0 00-.45.3z"
            fill="#0078D4"
          />
          <path
            d="M9.54 7.13L3.05 18.05a.5.5 0 00.44.7h5.19a.68.68 0 00.59-.38l5.58-11.24H9.99a.5.5 0 00-.45.28v-.28z"
            fill="#0078D4"
            opacity="0.8"
          />
        </svg>
      );
    }

    // GCP
    if (providerKey === "gcp") {
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12.19 5h-.38a7.25 7.25 0 00-6.28 10.87l3.19-5.53a3.63 3.63 0 013.47-2.51z"
            fill="#EA4335"
          />
          <path
            d="M19.53 8.91a7.18 7.18 0 00-2.87-3.22l-3.19 5.53a3.63 3.63 0 011.3 4.19h3.62a7.26 7.26 0 001.14-6.5z"
            fill="#4285F4"
          />
          <path
            d="M5.53 15.87a7.2 7.2 0 005.85 3.41l3.19-5.53a3.63 3.63 0 01-3.47-.93l-3.62-.01a7.34 7.34 0 00-1.95 3.06z"
            fill="#34A853"
          />
          <path
            d="M8.77 15.41l-3.19 5.53h6.88l3.19-5.53z"
            fill="#FBBC05"
          />
        </svg>
      );
    }

    // DigitalOcean
    if (providerKey === "digitalocean") {
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 4C7.58 4 4 7.58 4 12h4.5c0-1.93 1.57-3.5 3.5-3.5V4z"
            fill="#0080FF"
          />
          <path
            d="M12 20v-4.5c1.93 0 3.5-1.57 3.5-3.5H20c0 4.42-3.58 8-8 8z"
            fill="#0080FF"
          />
          <path d="M8.5 15.5H12V12H8.5v3.5z" fill="#0080FF" />
          <path d="M5.5 18.5H8.5V15.5H5.5v3z" fill="#0080FF" />
          <path d="M3 21h2.5v-2.5H3V21z" fill="#0080FF" />
        </svg>
      );
    }

    // Cloudflare
    if (providerKey === "cloudflare") {
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M16.51 15.97l.39-1.29c.17-.58.09-1.12-.23-1.52-.3-.37-.77-.58-1.32-.6l-8.3-.11a.25.25 0 01-.2-.09.27.27 0 01-.05-.22.34.34 0 01.3-.25l8.41-.11c1.46-.09 3.05-1.27 3.58-2.68l.67-1.75a.52.52 0 00.03-.18 6.63 6.63 0 00-12.55 2.19c-.67-.49-1.52-.74-2.45-.64-1.61.18-2.9 1.52-3.03 3.14-.03.42 0 .83.09 1.22-2 .08-3.61 1.7-3.61 3.71 0 .12 0 .23.01.35.02.18.18.3.36.3h17.14c.15 0 .29-.1.33-.25l.42-1.42z"
            fill="#F38020"
          />
          <path
            d="M19.35 10.58a.15.15 0 00-.15-.03c-.04.02-.08.05-.1.1l-.55 1.82c-.17.58-.09 1.12.23 1.52.3.37.77.58 1.32.6l1.78.02c.08 0 .15.03.2.09.05.05.07.13.05.21a.34.34 0 01-.3.25l-1.88.02a3.54 3.54 0 00-3.58 2.68l-.16.53c-.03.11.05.22.17.22h6.3c.15 0 .28-.1.31-.25a5.89 5.89 0 00-3.64-7.78z"
            fill="#FAAE40"
          />
        </svg>
      );
    }

    // Generic cloud icon for other providers
    return (
      <Cloud
        className={cn(className, "flex-shrink-0")}
        style={{ color: providerInfo.color }}
      />
    );
  }

  // Non-cloud server
  return <Server className={cn(className, "text-muted-foreground flex-shrink-0")} />;
}

function PortCellDisplay({
  cell,
  isSensitive,
  isSelected,
  onClick,
}: {
  cell: PortCell;
  isSensitive: boolean;
  isSelected?: boolean;
  onClick?: () => void;
}) {
  if (cell.state !== "open") {
    return (
      <div className="h-8 w-full flex items-center justify-center">
        <div className="h-2 w-2 rounded-full bg-muted" />
      </div>
    );
  }

  // Determine cell style based on state
  const isOpen = cell.state === "open";
  const showWarning = isOpen && isSensitive && !cell.encrypted;
  const showCaution = isOpen && !cell.encrypted && !isSensitive;

  return (
    <button
      onClick={onClick}
      className={cn(
        "h-8 w-full flex items-center justify-center rounded transition-colors cursor-pointer",
        "hover:ring-2 hover:ring-offset-1 hover:ring-primary/50",
        isSelected && "ring-2 ring-offset-1 ring-primary",
        showWarning && "bg-red-500/20 border border-red-500/50",
        showCaution && "bg-yellow-500/20 border border-yellow-500/50",
        isOpen && cell.encrypted && "bg-green-500/20 border border-green-500/50",
        !isOpen && "bg-muted/50 cursor-default"
      )}
      title={`Port ${cell.port}: ${cell.state}${cell.service ? ` (${cell.service})` : ""}${cell.encrypted ? " - Encrypted" : ""} - Click for details`}
    >
      {isOpen && (
        <>
          {cell.encrypted ? (
            <Lock className="h-3 w-3 text-green-600" />
          ) : showWarning ? (
            <AlertTriangle className="h-3 w-3 text-red-600" />
          ) : (
            <LockOpen className="h-3 w-3 text-yellow-600" />
          )}
        </>
      )}
      {cell.cve_count > 0 && (
        <Badge
          variant="destructive"
          className="absolute -top-1 -right-1 h-4 w-4 p-0 text-[10px] flex items-center justify-center"
        >
          {cell.cve_count}
        </Badge>
      )}
    </button>
  );
}

function PortDetailsPanel({
  cell,
  ipRow,
  onClose,
}: {
  cell: PortCell;
  ipRow: IpRow;
  onClose: () => void;
}) {
  const severity = getPortSeverity(cell.port, cell.encrypted);
  const recommendation = securityRecommendations[cell.port];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Server className="h-5 w-5" />
            Port {cell.port}/{cell.protocol}
          </h3>
          <p className="text-sm text-muted-foreground">
            {portDescriptions[cell.port] || "Unknown Service"} on {ipRow.ip}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-6">
        {/* Severity Badge */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Risk Level:</span>
          <Badge
            variant={
              severity === "critical" || severity === "high"
                ? "destructive"
                : severity === "medium"
                ? "default"
                : severity === "low"
                ? "secondary"
                : "outline"
            }
            className={cn(
              severity === "medium" && "bg-yellow-500/20 text-yellow-700 border-yellow-500"
            )}
          >
            {severity.toUpperCase()}
          </Badge>
        </div>

        {/* Service Info */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Info className="h-4 w-4" />
            Service Information
          </h4>
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Port</span>
              <span className="text-sm font-mono">{cell.port}/{cell.protocol}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">State</span>
              <span className="text-sm font-medium text-green-500">{cell.state}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Service</span>
              <span className="text-sm font-mono">{cell.service || "Unknown"}</span>
            </div>
            {cell.service_version && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Version</span>
                <span className="text-sm font-mono">{cell.service_version}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Encrypted</span>
              <span className="text-sm">
                {cell.encrypted ? (
                  <span className="text-green-500 flex items-center gap-1">
                    <Lock className="h-3 w-3" /> Yes
                  </span>
                ) : (
                  <span className="text-yellow-500 flex items-center gap-1">
                    <LockOpen className="h-3 w-3" /> No
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Banner */}
        {cell.service_banner && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Service Banner</h4>
            <div className="bg-muted rounded-lg p-3 font-mono text-xs overflow-x-auto whitespace-pre-wrap break-all">
              {cell.service_banner}
            </div>
          </div>
        )}

        {/* Host Info */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <CloudProviderIcon
              isCloud={ipRow.is_cloud}
              cloudProvider={ipRow.cloud_provider}
              asnName={ipRow.asn_name}
            />
            Host Information
          </h4>
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">IP Address</span>
              <span className="text-sm font-mono">{ipRow.ip}</span>
            </div>
            {ipRow.hostname && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Hostname</span>
                <span className="text-sm font-mono truncate max-w-[200px]">{ipRow.hostname}</span>
              </div>
            )}
            {ipRow.is_cloud && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Cloud Provider</span>
                <span className="text-sm font-medium flex items-center gap-1">
                  <Cloud className="h-3 w-3" />
                  {getCloudProviderInfo(ipRow.cloud_provider, ipRow.asn_name)?.name || ipRow.cloud_provider || "Unknown"}
                </span>
              </div>
            )}
            {ipRow.asn_name && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Network</span>
                <span className="text-sm truncate max-w-[180px]">{ipRow.asn_name}</span>
              </div>
            )}
            {ipRow.country_code && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Country</span>
                <span className="text-sm">{ipRow.country_code}</span>
              </div>
            )}
          </div>
        </div>

        {/* Related Subdomains */}
        {ipRow.subdomains && ipRow.subdomains.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Related Subdomains ({ipRow.subdomains.length})
            </h4>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2 max-h-40 overflow-y-auto">
              {ipRow.subdomains.map((sub) => (
                <div key={sub} className="flex items-center gap-2 text-sm font-mono text-blue-500">
                  <Globe className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{sub}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Security Recommendation */}
        {recommendation && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Security Recommendation
            </h4>
            <div
              className={cn(
                "rounded-lg p-4 text-sm",
                severity === "critical" && "bg-red-500/10 border border-red-500/20",
                severity === "high" && "bg-orange-500/10 border border-orange-500/20",
                severity === "medium" && "bg-yellow-500/10 border border-yellow-500/20",
                severity === "low" && "bg-blue-500/10 border border-blue-500/20",
                severity === "safe" && "bg-green-500/10 border border-green-500/20"
              )}
            >
              {recommendation}
            </div>
          </div>
        )}

        {/* Link to Asset */}
        {cell.asset_id && (
          <Link
            to={`/assets/${cell.asset_id}`}
            className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            View Full Asset Details
          </Link>
        )}
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  description,
  highlight,
}: {
  title: string;
  value: number;
  description: string;
  highlight?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-sm font-medium text-muted-foreground">{title}</div>
        <div
          className={cn(
            "text-2xl font-bold mt-1",
            highlight && value > 0 && "text-red-600 dark:text-red-400"
          )}
        >
          {value}
        </div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </CardContent>
    </Card>
  );
}

function countOpenPorts(matrix: ServiceMatrixType): number {
  let count = 0;
  for (const ip of matrix.ips) {
    for (const cell of ip.cells) {
      if (cell.state === "open") count++;
    }
  }
  return count;
}

function countSensitivePorts(matrix: ServiceMatrixType): number {
  let count = 0;
  for (const ip of matrix.ips) {
    for (const cell of ip.cells) {
      if (cell.state === "open" && sensitivePorts.has(cell.port)) count++;
    }
  }
  return count;
}

function countUnencryptedPorts(matrix: ServiceMatrixType): number {
  let count = 0;
  for (const ip of matrix.ips) {
    for (const cell of ip.cells) {
      if (cell.state === "open" && !cell.encrypted) count++;
    }
  }
  return count;
}
